import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';
import { getService } from '@Client/runner.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';
import FlowModelType from '@Shared.Angular/@types/core/contracts/constants/flowModelType';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import { CountdownTimer } from '@Shared.Angular/flowingly.components/CountdownTimer/CountdownTimer';
import {
  RecordingPhase,
  VIDEO_RECORDING_COMPLETE,
  VIDEO_RECORDING_PHASE_CHANGED
} from '@Client/runner.services/video.recording.service';
import { FlowModelGenerationFileService } from '@Shared.Angular/flowingly.services/flowModel.generationFile.service';

enum UploadStatus {
  NotUploaded = 'Not Uploaded',
  Uploading = 'Uploading',
  Uploaded = 'Uploaded'
}

enum TargetType {
  ProcessMap = 'Process Map',
  Workflow = 'Workflow',
  Procedure = 'SOP'
}

export const RecordVideoModal = () => {
  const videoRecordingService = getService('videoRecordingService');
  const pubsubService = getService('pubsubService');
  const tempModelerUrlService = getService('tempModelerUrlService');
  const procedureApiService = getService('procedureApiService');
  const appConfig = getService('APP_CONFIG');
  const $state = getService('$state');

  const [phase, setPhase] = useState<RecordingPhase>(
    videoRecordingService.getRecordingPhase()
  );
  const [video, setVideo] = useState<Blob | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(
    UploadStatus.NotUploaded
  );
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [targetType, setTargetType] = useState<TargetType | null>(null);
  const [procedureName, setProcedureName] = useState<string>('');

  useEffect(() => {
    const subscriberId = 'flowingly.runner.record-video-modal';
    pubsubService.subscribe<RecordingPhase>(
      VIDEO_RECORDING_PHASE_CHANGED,
      (_event, newPhase) => setPhase(newPhase),
      subscriberId
    );
    pubsubService.subscribe<Blob>(
      VIDEO_RECORDING_COMPLETE,
      (_event, blob) => {
        setVideo(blob);
        const name = `recording-${new Date()
          .toISOString()
          .replace(/[:.]/g, '-')}.webm`;
        setFileName(name);
      },
      subscriberId
    );
    return () => {
      pubsubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const downloadUrl = useMemo(
    () => (video ? URL.createObjectURL(video) : null),
    [video]
  );

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  const createFlowModel = useCallback(
    (flowModelType: FlowModelType) => {
      const generationFileService = getService(
        'flowModelGenerationFileService'
      );
      const file = new File([video], fileName, { type: 'video/webm' });
      const localFile = generationFileService.createLocalFile(file);
      setErrorMessage('');
      setUploadStatus(UploadStatus.Uploading);
      generationFileService
        .uploadFile(file, localFile.localId)
        .then((uploadedFile) => {
          tempModelerUrlService.openModeler({
            flowModelType: flowModelType,
            generateWithAi: true,
            generationFileId: uploadedFile.id
          });
          setUploadStatus(UploadStatus.Uploaded);
        })
        .catch((error: { message?: string }) => {
          setErrorMessage(error?.message ?? 'Upload failed.');
          setUploadStatus(UploadStatus.NotUploaded);
        });
    },
    [video]
  );

  const closeModal = useCallback(() => {
    videoRecordingService.resetPhase();
    setUploadStatus(UploadStatus.NotUploaded);
    setErrorMessage('');
    setFileName('');
    setTargetType(null);
    setProcedureName('');
    setVideo(null);
  }, []);

  const createProcedure = useCallback(() => {
    const videoFile = new File([video], fileName, { type: 'video/webm' });
    setUploadStatus(UploadStatus.Uploading);
    setErrorMessage('');
    procedureApiService
      .createProcedureFromVideo(videoFile, { name: procedureName.trim() })
      .then((procedureId: Guid) => {
        setUploadStatus(UploadStatus.Uploaded);
        $state.go(STATE_MAPPINGS.procedure, {
          procedureId,
          viewMode: ProcedureViewMode.Edit
        });
      })
      .catch((error: { data?: { message?: string } }) => {
        setErrorMessage(error?.data?.message ?? 'Failed to create SOP.');
        setUploadStatus(UploadStatus.NotUploaded);
      });
  }, [video, procedureName]);

  const createTarget = useCallback(() => {
    if (!video) {
      setErrorMessage('No video to upload.');
      return;
    }
    if (video.size > FlowModelGenerationFileService.maxVideoFileSizeBytes) {
      setErrorMessage(
        'Video file is too large. Please record a shorter video.'
      );
      return;
    }
    switch (targetType) {
      case TargetType.ProcessMap:
        createFlowModel(FlowModelType.ProcessMap);
        break;
      case TargetType.Workflow:
        createFlowModel(FlowModelType.Workflow);
        break;
      case TargetType.Procedure: {
        createProcedure();
        break;
      }
      default:
        break;
    }
  }, [targetType, createFlowModel, createProcedure]);

  if (
    phase === RecordingPhase.Inactive ||
    phase === RecordingPhase.AcquiringMedia ||
    phase === RecordingPhase.Recording
  ) {
    return null;
  }

  if (phase === RecordingPhase.Countdown) {
    return (
      <CountdownTimer
        title="Recording will start in"
        seconds={3}
        onCountdownFinished={() => videoRecordingService.startRecording()}
      />
    );
  }

  const actionButtonText = targetType ? `Create ${targetType}` : 'Create';
  const isUploading = uploadStatus === UploadStatus.Uploading;
  const isUploaded = uploadStatus === UploadStatus.Uploaded;
  const missingProcedureName =
    targetType === TargetType.Procedure && !procedureName.trim();
  return (
    <Modal
      title="Recorded Video"
      onClose={!isUploading ? closeModal : undefined}
      onCancelClick={!isUploading ? closeModal : undefined}
      onActionClick={createTarget}
      actionButtonText={actionButtonText}
      isActionDisabled={
        !targetType || isUploading || isUploaded || missingProcedureName
      }
      hideCancelButton={isUploading}
      cancelButtonText="Close"
      size="md"
    >
      <div className="record-video-modal">
        {downloadUrl && (
          <p>
            <a
              className="record-video-modal__download-link"
              href={downloadUrl}
              download={fileName}
              data-testid="record-video-download-link"
            >
              <i className="fa-light fa-download"></i> Download video
            </a>
          </p>
        )}
        {video && uploadStatus !== UploadStatus.Uploaded && (
          <p>Choose what you'd like to create from this recording.</p>
        )}
        {video && uploadStatus !== UploadStatus.Uploaded && (
          <div className="record-video-modal__actions">
            {appConfig.enableProcedures && (
              <>
                <TextButton
                  headerText="Create SOP"
                  text="Turn the recording into a step-by-step standard operating procedure."
                  type={
                    targetType === TargetType.Procedure ? 'primary' : 'tertiary'
                  }
                  outlineOnly={true}
                  onClick={() => setTargetType(TargetType.Procedure)}
                  testId="record-video-create-procedure"
                />
                {targetType === TargetType.Procedure && (
                  <div className="flowingly-field">
                    <label>SOP name</label>
                    <input
                      type="text"
                      value={procedureName}
                      onChange={(e) => setProcedureName(e.target.value)}
                      data-testid="record-video-procedure-name"
                    />
                  </div>
                )}
              </>
            )}
            {appConfig.allowProcessMap && (
              <TextButton
                headerText="Create Process Map"
                text="Generate a visual flow diagram of the steps captured in the video."
                type={
                  targetType === TargetType.ProcessMap ? 'primary' : 'tertiary'
                }
                outlineOnly={true}
                onClick={() => setTargetType(TargetType.ProcessMap)}
                testId="record-video-create-process-map"
              />
            )}
            {appConfig.allowWorkflow && (
              <TextButton
                headerText="Create Workflow"
                text="Generate an executable workflow with tasks, approvals, and assignees."
                type={
                  targetType === TargetType.Workflow ? 'primary' : 'tertiary'
                }
                outlineOnly={true}
                onClick={() => setTargetType(TargetType.Workflow)}
                testId="record-video-create-workflow"
              />
            )}
          </div>
        )}
        {isUploading && (
          <p
            className="record-video-modal__uploading"
            data-testid="record-video-upload-status"
          >
            Creating {targetType}, please wait...
          </p>
        )}
        {errorMessage && (
          <p
            className="record-video-modal__error"
            data-testid="record-video-upload-error"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
};
